import { useEffect, useRef, useState } from "react";
import { useTheme } from "@emotion/react";
import { Close, Print } from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import { CampoDeTexto, CampoDeSelecao } from "../CampoInput";
import { useGerenciadorFiscalService } from "../../services/gerenciadorFiscalService";
import extrairMensagemErro from "../../utils/extrairMensagemErro";
import extrairMensagemErroArquivo from "../../utils/extrairMensagemErroArquivo";
import useTamanhoTela from "../../utils/useTamanhoTela";
import { formatarCPF } from "../../utils/formatarCpf";
import formatarCNPJ from "../../utils/formatarCNPJ";
import formatarReal from "../../utils/formatarReal";
import * as S from "./styles";
import { UFS, OPERACOES_NFCE, OPERACOES_NFE, TITULO, OPERACOES_DESTRUTIVAS, UFS_INUTILIZACAO_DESATIVADA } from "./enums";
import { useAuth } from "../../contexts/auth";
import podeFecharAoClicarFora from "../../utils/fecharAoClicarFora";

function extrairTagXml(xml, tag) {
  const matches = [...(xml?.matchAll(new RegExp(`<${tag}>([^<]*)</${tag}>`, "g")) || [])];
  return matches.length ? matches[matches.length - 1][1] : undefined;
}

function formatarDataHoraSefaz(dhRecbto) {
  if (!dhRecbto) return undefined;
  const data = new Date(dhRecbto);
  if (Number.isNaN(data.getTime())) return dhRecbto;
  return data.toLocaleString("pt-BR");
}

function operacoesPorStatus(modeloFiscal, status) {
  const sigla = modeloFiscal === "NFCE" ? "NFC-e" : "NF-e";
  const statusOp = { valor: "STATUS", label: "Consultar status do serviço" };
  const situacaoOp = { valor: "SITUACAO", label: "Consultar situação" };
  const cceOp = { valor: "CCE", label: "Carta de correção (CC-e)" };
  const cancelamentoOp = { valor: "CANCELAMENTO", label: `Cancelar ${sigla}` };
  const autorizacaoOp = { valor: "AUTORIZACAO", label: `Autorizar ${sigla}` };
  const podeCCE = modeloFiscal !== "NFCE";

  if (status === "AUTORIZADO") {
    return [statusOp, situacaoOp, ...(podeCCE ? [cceOp] : []), cancelamentoOp];
  }
  if (status === "CANCELADO") {
    return [statusOp, situacaoOp, ...(podeCCE ? [cceOp] : [])];
  }
  return [autorizacaoOp, statusOp];
}

export default function ModalGerenciadorFiscal({
  modeloInicial,
  operacaoInicial,
  numeroNotaInicial,
  valorNotaInicial,
  idNota,
  status,
  dadosNota,
  onFechar,
  onAutorizada,
}) {
  const theme = useTheme();
  const { largura } = useTamanhoTela();
  const service = useGerenciadorFiscalService();
  const isMobile = largura <= 768;
  const { dadosAuth } = useAuth();

  const inutilizacaoDesativada = UFS_INUTILIZACAO_DESATIVADA.includes(dadosAuth?.empresaDTO?.uf);
  const operacaoDesabilitada = (valor) => valor === "INUTILIZACAO" && inutilizacaoDesativada;

  const [modeloFiscal, setModeloFiscal] = useState(modeloInicial || "NFE");
  const [statusAtual, setStatusAtual] = useState(status);
  const operacoes = idNota
    ? operacoesPorStatus(modeloFiscal, statusAtual)
    : modeloFiscal === "NFCE"
      ? OPERACOES_NFCE
      : OPERACOES_NFE;

  const [operacao, setOperacao] = useState(
    operacaoInicial || operacoes.find((op) => !operacaoDesabilitada(op.valor)).valor
  );
  const [series, setSeries] = useState({ nfe: [], nfce: [] });
  const [carregandoSeries, setCarregandoSeries] = useState(false);

  const [serie, setSerie] = useState("");
  const [nInicial, setNInicial] = useState("");
  const [nFinal, setNFinal] = useState("");
  const [motivo, setMotivo] = useState("");

  const [tipoPessoa, setTipoPessoa] = useState("JURIDICA");
  const [uf, setUf] = useState("");
  const [documento, setDocumento] = useState("");

  const [chaveNfce, setChaveNfce] = useState("");
  const [numeroNota, setNumeroNota] = useState(numeroNotaInicial || idNota || "");
  const [valorNota, setValorNota] = useState(valorNotaInicial || "");
  const [chaveAcesso, setChaveAcesso] = useState("");

  const [justificativaCancelamento, setJustificativaCancelamento] = useState("");

  const [textoCorrecao, setTextoCorrecao] = useState("");

  const [retornoSefaz, setRetornoSefaz] = useState("");
  const [executando, setExecutando] = useState(false);
  const [imprimindo, setImprimindo] = useState(false);
  const [autorizada, setAutorizada] = useState(false);
  const [erros, setErros] = useState({});
  const [confirmacaoAberta, setConfirmacaoAberta] = useState(false);

  useEffect(() => {
    carregarSeries();
  }, []);

  const primeiraRenderizacaoOperacao = useRef(true);
  useEffect(() => {
    if (primeiraRenderizacaoOperacao.current) {
      primeiraRenderizacaoOperacao.current = false;
      return;
    }
    limparCampos();
    setAutorizada(false);
  }, [operacao]);

  function handleModeloChange(novoModelo) {
    if (novoModelo === modeloFiscal) return;
    setModeloFiscal(novoModelo);
    const novasOperacoes = novoModelo === "NFCE" ? OPERACOES_NFCE : OPERACOES_NFE;
    setOperacao(novasOperacoes.find((op) => !operacaoDesabilitada(op.valor)).valor);
    limparCampos();
    setAutorizada(false);
  }

  async function carregarSeries() {
    setCarregandoSeries(true);
    try {
      const data = await service.buscarSeries();
      if (data && typeof data === "object" && !Array.isArray(data)) {
        setSeries({
          nfe: Array.isArray(data.nfe) ? data.nfe : [],
          nfce: Array.isArray(data.nfce) ? data.nfce : [],
        });
      } else {
        setSeries({ nfe: [], nfce: [] });
      }
    } catch (err) {
      setSeries({ nfe: [], nfce: [] });
      toast.error(extrairMensagemErro(err, "Erro ao carregar as séries fiscais."));
    } finally {
      setCarregandoSeries(false);
    }
  }

  function limparCampos() {
    setSerie("");
    setNInicial("");
    setNFinal("");
    setMotivo("");
    setTipoPessoa("JURIDICA");
    setUf("");
    setDocumento("");
    setChaveNfce("");
    if (!idNota) {
      setNumeroNota("");
      setValorNota("");
      setChaveAcesso("");
    }
    setJustificativaCancelamento("");
    setTextoCorrecao("");
    setErros({});
    setRetornoSefaz("");
    setConfirmacaoAberta(false);
  }

  function seriesFiltradas() {
    const lista = modeloFiscal === "NFE" ? series.nfe : series.nfce;
    return (lista || []).map((s) => ({
      nome: String(s.serie || s.numero || s),
      valor: String(s.serie || s.numero || s),
    }));
  }

  function handleDocumentoChange(e) {
    if (tipoPessoa === "FISICA") {
      const raw = e.target.value.replace(/\D/g, "");
      setDocumento(formatarCPF(raw.slice(0, 11)));
    } else {
      setDocumento(formatarCNPJ(e.target.value));
    }
  }

  function validar() {
    const novos = {};

    if (operacao === "AUTORIZACAO") {
      if (!numeroNota) novos.numeroNota = "Número da nota obrigatório";
    }

    if (operacao === "CANCELAMENTO") {
      if (!numeroNota) novos.numeroNota = "Número da nota obrigatório";
      if (!justificativaCancelamento) novos.justificativaCancelamento = "Justificativa obrigatória";
      else if (justificativaCancelamento.length < 15)
        novos.justificativaCancelamento = "Mínimo de 15 caracteres";
      else if (justificativaCancelamento.length > 255)
        novos.justificativaCancelamento = "Máximo de 255 caracteres";
    }

    if (operacao === "INUTILIZACAO") {
      if (!serie) novos.serie = "Série obrigatória";
      if (!nInicial) novos.nInicial = "Nº inicial obrigatório";
      if (!nFinal) novos.nFinal = "Nº final obrigatório";
      if (nInicial && nFinal && Number(nFinal) < Number(nInicial))
        novos.nFinal = "Deve ser ≥ ao Nº inicial";
      if (!motivo) novos.motivo = "Motivo obrigatório";
      else if (motivo.length < 15) novos.motivo = "Mínimo de 15 caracteres";
      else if (motivo.length > 255) novos.motivo = "Máximo de 255 caracteres";
    }

    if (operacao === "SITUACAO" && !idNota) {
      if (!chaveNfce) novos.chaveNfce = "Chave de acesso obrigatória";
      else if (chaveNfce.length !== 44) novos.chaveNfce = "Chave de acesso inválida (44 dígitos)";
    }

    if (operacao === "CCE") {
      if (!textoCorrecao) novos.textoCorrecao = "Texto da correção obrigatório";
      else if (textoCorrecao.length < 15) novos.textoCorrecao = "Mínimo de 15 caracteres";
      else if (textoCorrecao.length > 255) novos.textoCorrecao = "Máximo de 255 caracteres";
    }

    if (operacao === "CONTRIBUINTE") {
      if (!uf) novos.uf = "Estado obrigatório";
      if (!documento) novos.documento = "Documento obrigatório";
      else if (tipoPessoa === "FISICA") {
        const nums = documento.replace(/\D/g, "");
        if (nums.length !== 11) novos.documento = "CPF inválido (11 dígitos)";
      } else {
        const alfanum = documento.replace(/[^a-zA-Z0-9]/g, "");
        if (alfanum.length !== 14) novos.documento = "CNPJ inválido (14 caracteres)";
      }
    }

    setErros(novos);
    return Object.keys(novos).length === 0;
  }

  function executar() {
    if (operacaoDesabilitada(operacao)) return;
    if (!validar()) return;
    if (OPERACOES_DESTRUTIVAS.includes(operacao)) {
      setConfirmacaoAberta(true);
      return;
    }
    executarOperacao();
  }

  async function executarOperacao() {
    setExecutando(true);
    setRetornoSefaz("");
    try {
      let resultado;
      const idNotaParaApi = idNota || numeroNota;

      if (operacao === "AUTORIZACAO") {
        resultado = await service.autorizarNfe({ idNota: idNotaParaApi, modeloFiscal });
        if (!resultado?.chave) {
          throw Object.assign(
            new Error(
              "A SEFAZ não retornou confirmação de autorização (sem chave de acesso/protocolo). A nota provavelmente NÃO foi autorizada - tente novamente ou verifique com o suporte.",
            ),
            { semRespostaValidaBackend: true },
          );
        }
        setAutorizada(true);
        setStatusAtual("AUTORIZADO");
        setChaveAcesso(resultado?.chave || "");
        toast.success("NF-e autorizada com sucesso.");
        onAutorizada?.({ idNota: idNotaParaApi, chave: resultado.chave });
      } else if (operacao === "CANCELAMENTO") {
        resultado = await service.cancelarNfe({
          idNota: idNotaParaApi,
          motivoCancelamento: justificativaCancelamento,
          modeloFiscal,
        });
        toast.success("NF-e cancelada com sucesso!");
        setStatusAtual("CANCELADO");
      } else if (operacao === "INUTILIZACAO") {
        resultado = await service.inutilizar({ serie, nInicial, nFinal, motivo, modeloFiscal });
        toast.success("Inutilização realizada com sucesso!");
      } else if (operacao === "STATUS") {
        resultado = await service.consultarStatusServico({ modeloFiscal });
      } else if (operacao === "SITUACAO") {
        resultado = idNota
          ? await service.consultarSituacaoPorId(idNota)
          : await service.consultarSituacao({ chaveAcesso: chaveNfce });
      } else if (operacao === "CONTRIBUINTE") {
        const documentoLimpo =
          tipoPessoa === "FISICA"
            ? documento.replace(/\D/g, "")
            : documento.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
        resultado = await service.consultarCadastroContribuinte({
          documento: documentoLimpo,
          uf,
        });
      } else if (operacao === "CCE") {
        resultado = await service.emitirCartaCorrecao({ idNota, textoCorrecao });
        toast.success("Carta de correção registrada com sucesso!");
      }

      if (operacao === "AUTORIZACAO") {
        const xmlResposta = resultado?.xmlRetorno || resultado?.xml;
        const protocolo = extrairTagXml(xmlResposta, "nProt");
        const dataAutorizacao = formatarDataHoraSefaz(extrairTagXml(xmlResposta, "dhRecbto"));
        const xMotivo = extrairTagXml(xmlResposta, "xMotivo") || "Autorizado o uso da NF-e";
        let texto = `NF-e autorizada com sucesso!\nStatus: ${xMotivo}`;
        texto += `\nChave de acesso: ${resultado.chave}`;
        if (protocolo) texto += `\nProtocolo de autorização: ${protocolo}`;
        if (dataAutorizacao) texto += `\nData/hora de autorização: ${dataAutorizacao}`;
        setRetornoSefaz(texto);
      } else if (operacao === "CANCELAMENTO") {
        const xmlResposta = resultado?.xmlRetorno || resultado?.xml;
        const protocolo = extrairTagXml(xmlResposta, "nProt");
        const dataCancelamento = formatarDataHoraSefaz(
          extrairTagXml(xmlResposta, "dhRecbto") || extrairTagXml(xmlResposta, "dhRegEvento"),
        );
        const xMotivo = extrairTagXml(xmlResposta, "xMotivo") || "Cancelamento homologado";
        let texto = `NF-e cancelada com sucesso!\nStatus: ${xMotivo}`;
        if (resultado?.chaveCancelamento) {
          texto += `\nChave de cancelamento: ${resultado.chaveCancelamento}`;
        }
        if (protocolo) texto += `\nProtocolo de cancelamento: ${protocolo}`;
        if (dataCancelamento) texto += `\nData/hora de cancelamento: ${dataCancelamento}`;
        setRetornoSefaz(texto);
      } else if (operacao === "STATUS") {
        const cstat = resultado?.cstat ?? resultado?.cStat ?? resultado?.CStat;
        const xmotivo = resultado?.xmotivo ?? resultado?.xMotivo ?? resultado?.XMotivo;
        setRetornoSefaz(`Consultando status do serviço:\nStatus: ${cstat} - ${xmotivo}`);
      } else if (operacao === "SITUACAO") {
        const sigla = modeloFiscal === "NFCE" ? "NFC-e" : "NF-e";
        const cstat = resultado?.cstat ?? resultado?.cStat ?? resultado?.CStat;
        const xmotivo = resultado?.xmotivo ?? resultado?.xMotivo ?? resultado?.XMotivo;
        setRetornoSefaz(`Consultando situação da ${sigla}\nStatus: ${cstat} - ${xmotivo}`);
      } else if (operacao === "CCE") {
        const protocolo = extrairTagXml(resultado, "nProt");
        const dataEvento = formatarDataHoraSefaz(extrairTagXml(resultado, "dhRegEvento"));
        const xMotivo = extrairTagXml(resultado, "xMotivo") || "Carta de correção registrada";
        let texto = `Carta de correção registrada com sucesso!\nStatus: ${xMotivo}`;
        if (protocolo) texto += `\nProtocolo: ${protocolo}`;
        if (dataEvento) texto += `\nData/hora do evento: ${dataEvento}`;
        setRetornoSefaz(texto);
      } else if (operacao === "CONTRIBUINTE") {
        const infCons = resultado?.infCons;
        const cstat = infCons?.cstat ?? infCons?.cStat ?? infCons?.CStat;
        const xmotivo = infCons?.xmotivo ?? infCons?.xMotivo ?? infCons?.XMotivo;
        let texto = `Consultando cadastro do contribuinte:\nStatus: ${cstat} - ${xmotivo}`;
        (infCons?.infCad || []).forEach((cadastro) => {
          texto += `\nRazão Social: ${cadastro?.xnome}`;
          texto += `\nCNPJ: ${cadastro?.cnpj || cadastro?.cpf}`;
          texto += `\nIE: ${cadastro?.ie}`;
        });
        setRetornoSefaz(texto);
      } else {
        setRetornoSefaz(
          typeof resultado === "string" ? resultado : JSON.stringify(resultado, null, 2),
        );
      }
    } catch (error) {
      const msg = error?.semRespostaValidaBackend
        ? error.message
        : extrairMensagemErro(
            error,
            operacao === "AUTORIZACAO"
              ? "Não foi possível autorizar a NF-e. Verifique o retorno da SEFAZ e tente novamente."
              : "Erro ao comunicar com a SEFAZ",
          );
      setRetornoSefaz(`Erro: ${msg}`);
      toast.error(msg);
    } finally {
      setExecutando(false);
    }
  }

  async function handleImprimir() {
    setImprimindo(true);
    try {
      const pdf = await service.gerarDanfe(idNota || numeroNota);
      const blobPDF = new Blob([pdf], { type: "application/pdf" });
      const urlPDF = URL.createObjectURL(blobPDF);
      window.open(urlPDF, "_blank");
    } catch (error) {
      toast.error(extrairMensagemErroArquivo(error, "Erro ao gerar o DANFE."));
    } finally {
      setImprimindo(false);
    }
  }

  function cancelar() {
    limparCampos();
    onFechar();
  }

  const itensSerieSelect = seriesFiltradas();

  function renderAutorizacao() {
    return (
      <>
        <S.LinhaFormulario>
          <S.CampoWrapper $flex={1}>
            <CampoDeTexto
              titulo="Número da nota"
              value={numeroNota}
              onChange={(e) => setNumeroNota(e.target.value.replace(/\D/g, ""))}
              error={!!erros.numeroNota}
              inputProps={{ inputMode: "numeric", readOnly: !!idNota }}
            />
            {erros.numeroNota && <S.MensagemErro>{erros.numeroNota}</S.MensagemErro>}
          </S.CampoWrapper>
          <S.CampoWrapper $flex={1}>
            <CampoDeTexto
              titulo="Valor"
              value={valorNota}
              onChange={(e) => setValorNota(e.target.value)}
              inputProps={{ inputMode: "decimal", readOnly: !!idNota }}
            />
          </S.CampoWrapper>
        </S.LinhaFormulario>
        <S.CampoWrapper>
          <CampoDeTexto
            titulo="Chave de acesso"
            value={chaveAcesso}
            inputProps={{ readOnly: true }}
          />
          {!autorizada && <S.TextoAjuda>Disponível após autorizar a NF-e</S.TextoAjuda>}
        </S.CampoWrapper>
      </>
    );
  }

  function renderCancelamento() {
    return (
      <>
        <S.CampoWrapper>
          <CampoDeTexto
            titulo="Número da nota"
            value={numeroNota}
            onChange={(e) => setNumeroNota(e.target.value.replace(/\D/g, ""))}
            error={!!erros.numeroNota}
            inputProps={{ inputMode: "numeric", readOnly: !!idNota }}
          />
          {erros.numeroNota && <S.MensagemErro>{erros.numeroNota}</S.MensagemErro>}
        </S.CampoWrapper>
        <S.CampoWrapper>
          <CampoDeTexto
            titulo="Justificativa do cancelamento"
            value={justificativaCancelamento}
            onChange={(e) => setJustificativaCancelamento(e.target.value.slice(0, 255))}
            error={!!erros.justificativaCancelamento}
            maxlength={255}
            multiline
            rows={3}
            style={{ height: "auto" }}
            sx={{
              "& .MuiInputBase-root": { height: "auto !important", minHeight: "100px !important" },
              "& .MuiInputBase-input.MuiInputBase-inputMultiline": {
                paddingTop: "24px",
                paddingLeft: "12px",
              },
            }}
          />
          {erros.justificativaCancelamento ? (
            <S.MensagemErro>{erros.justificativaCancelamento}</S.MensagemErro>
          ) : (
            <S.TextoAjuda>{`${justificativaCancelamento.length}/255 — mínimo 15 caracteres`}</S.TextoAjuda>
          )}
        </S.CampoWrapper>
      </>
    );
  }

  function renderCartaCorrecao() {
    return (
      <S.CampoWrapper>
        <CampoDeTexto
          titulo="Texto da correção"
          value={textoCorrecao}
          onChange={(e) => setTextoCorrecao(e.target.value.slice(0, 255))}
          error={!!erros.textoCorrecao}
          maxlength={255}
          multiline
          rows={3}
          style={{ height: "auto" }}
          sx={{
            "& .MuiInputBase-root": { height: "auto !important", minHeight: "100px !important" },
            "& .MuiInputBase-input.MuiInputBase-inputMultiline": {
              paddingTop: "24px",
              paddingLeft: "12px",
            },
          }}
        />
        {erros.textoCorrecao ? (
          <S.MensagemErro>{erros.textoCorrecao}</S.MensagemErro>
        ) : (
          <S.TextoAjuda>{`${textoCorrecao.length}/255 — mínimo 15 caracteres`}</S.TextoAjuda>
        )}
      </S.CampoWrapper>
    );
  }

  function renderInutilizacao() {
    return (
      <>
        <S.CampoWrapper>
          <CampoDeSelecao
            titulo="Série"
            valor={serie}
            onChange={(e) => setSerie(e.target.value)}
            items={itensSerieSelect}
            referenciaNome="nome"
            referenciaValor="valor"
            error={!!erros.serie}
            campoVazio={false}
            disabled={carregandoSeries}
          />
          {erros.serie && <S.MensagemErro>{erros.serie}</S.MensagemErro>}
        </S.CampoWrapper>

        <S.LinhaFormulario>
          <S.CampoWrapper $flex={1}>
            <CampoDeTexto
              titulo="Nº inicial"
              value={nInicial}
              onChange={(e) => setNInicial(e.target.value.replace(/\D/g, ""))}
              error={!!erros.nInicial}
              inputProps={{ inputMode: "numeric" }}
            />
            {erros.nInicial && <S.MensagemErro>{erros.nInicial}</S.MensagemErro>}
          </S.CampoWrapper>

          <S.CampoWrapper $flex={1}>
            <CampoDeTexto
              titulo="Nº final"
              value={nFinal}
              onChange={(e) => setNFinal(e.target.value.replace(/\D/g, ""))}
              error={!!erros.nFinal}
              inputProps={{ inputMode: "numeric" }}
            />
            {erros.nFinal && <S.MensagemErro>{erros.nFinal}</S.MensagemErro>}
          </S.CampoWrapper>
        </S.LinhaFormulario>

        <S.CampoWrapper>
          <CampoDeTexto
            titulo="Motivo da inutilização"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            error={!!erros.motivo}
            maxlength={255}
            multiline
            rows={3}
            style={{ height: "auto" }}
            sx={{
              "& .MuiInputBase-root": { height: "auto !important", minHeight: "100px !important" },
              "& .MuiInputBase-input.MuiInputBase-inputMultiline": {
                paddingTop: "24px",
                paddingLeft: "12px",
              },
            }}
          />
          {erros.motivo ? (
            <S.MensagemErro>{erros.motivo}</S.MensagemErro>
          ) : (
            <S.TextoAjuda>{`${motivo.length}/255 — mínimo 15 caracteres`}</S.TextoAjuda>
          )}
        </S.CampoWrapper>
      </>
    );
  }

  function renderStatus() {
    return null;
  }

  function renderSituacao() {
    if (idNota) {
      const numeroExibido =
        numeroNota || (dadosNota?.numero != null ? String(dadosNota.numero) : "");
      const valorExibido = valorNota || dadosNota?.valor;
      const chaveExibida = chaveAcesso || dadosNota?.chave || "";
      return (
        <>
          <S.LinhaFormulario>
            <S.CampoWrapper $flex={1}>
              <CampoDeTexto titulo="Número" value={numeroExibido} inputProps={{ readOnly: true }} />
            </S.CampoWrapper>

            <S.CampoWrapper $flex={1}>
              <CampoDeTexto
                titulo="Valor"
                value={
                  valorExibido != null && valorExibido !== "" ? formatarReal(valorExibido) : ""
                }
                inputProps={{ readOnly: true }}
              />
            </S.CampoWrapper>
          </S.LinhaFormulario>

          <S.CampoWrapper>
            <CampoDeTexto
              titulo="Chave de Acesso"
              value={chaveExibida}
              inputProps={{ readOnly: true }}
            />
          </S.CampoWrapper>
        </>
      );
    }
    return (
      <S.CampoWrapper>
        <CampoDeTexto
          titulo={`Chave de acesso da ${modeloFiscal === "NFCE" ? "NFC-e" : "NF-e"}`}
          value={chaveNfce}
          onChange={(e) => setChaveNfce(e.target.value.replace(/\D/g, "").slice(0, 44))}
          error={!!erros.chaveNfce}
          inputProps={{ inputMode: "numeric" }}
        />

        <S.TextoAjuda>{`${chaveNfce.length}/44 dígitos`}</S.TextoAjuda>
        {erros.chaveNfce && <S.MensagemErro>{erros.chaveNfce}</S.MensagemErro>}
      </S.CampoWrapper>
    );
  }

  function renderContribuinte() {
    return (
      <>
        <S.LinhaFormulario>
          {[
            { valor: "JURIDICA", label: "Jurídica" },
            { valor: "FISICA", label: "Física" },
          ].map((tp) => (
            <S.ItemRadio
              key={tp.valor}
              onClick={() => {
                setTipoPessoa(tp.valor);
                setDocumento("");
                setErros((prev) => ({ ...prev, documento: undefined }));
              }}
            >
              <S.RadioCircle>
                <S.RadioDot $visivel={tipoPessoa === tp.valor} />
              </S.RadioCircle>

              <S.TextoRadio>{tp.label}</S.TextoRadio>
            </S.ItemRadio>
          ))}
        </S.LinhaFormulario>

        <S.LinhaFormulario>
          <S.CampoWrapper $flex={1}>
            <CampoDeSelecao
              titulo="Estado (UF)"
              valor={uf}
              onChange={(e) => setUf(e.target.value)}
              items={UFS.map((u) => ({ nome: u, valor: u }))}
              referenciaNome="nome"
              referenciaValor="valor"
              error={!!erros.uf}
              campoVazio={false}
            />
            {erros.uf && <S.MensagemErro>{erros.uf}</S.MensagemErro>}
          </S.CampoWrapper>

          <S.CampoWrapper $flex={2}>
            <CampoDeTexto
              titulo={tipoPessoa === "FISICA" ? "CPF" : "CNPJ"}
              value={documento}
              onChange={handleDocumentoChange}
              error={!!erros.documento}
              inputProps={
                tipoPessoa === "FISICA" ? { inputMode: "numeric" } : { inputMode: "text" }
              }
            />
            {erros.documento && <S.MensagemErro>{erros.documento}</S.MensagemErro>}
          </S.CampoWrapper>

          <S.CampoWrapper $flex={1}>
            <CampoDeSelecao
              titulo="Série"
              valor={serie}
              onChange={(e) => setSerie(e.target.value)}
              items={itensSerieSelect}
              referenciaNome="nome"
              referenciaValor="valor"
              campoVazio={false}
              disabled={carregandoSeries}
            />
          </S.CampoWrapper>
        </S.LinhaFormulario>
      </>
    );
  }

  function renderFormulario() {
    if (operacao === "AUTORIZACAO") return renderAutorizacao();
    if (operacao === "CANCELAMENTO") return renderCancelamento();
    if (operacao === "INUTILIZACAO") return renderInutilizacao();
    if (operacao === "STATUS") return renderStatus();
    if (operacao === "SITUACAO") return renderSituacao();
    if (operacao === "CONTRIBUINTE") return renderContribuinte();
    if (operacao === "CCE") return renderCartaCorrecao();
    return null;
  }

  const mostrarImprimir = operacao === "AUTORIZACAO" && autorizada;

  const cancelamentoConcluido = operacao === "CANCELAMENTO" && statusAtual === "CANCELADO";

  const mensagemConfirmacaoPrincipal =
    operacao === "CANCELAMENTO"
      ? `Deseja realmente cancelar a nota fiscal nº ${numeroNota}?`
      : operacao === "CCE"
        ? `Deseja realmente registrar esta carta de correção na nota fiscal nº ${numeroNota}?`
        : `Deseja realmente inutilizar a numeração de ${nInicial} a ${nFinal} (série ${serie})?`;

  const conteudo = (
    <S.Painel
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <S.Cabecalho>
        <S.Titulo>{TITULO[modeloFiscal]}</S.Titulo>

        <S.BotaoFechar onClick={cancelar} aria-label="Fechar">
          <Close style={{ fontSize: 24, color: theme.cores.semanticDanger2 }} />
        </S.BotaoFechar>
      </S.Cabecalho>

      <S.Corpo>
        {!idNota && (
          <S.LinhaModelos>
            {[
              { valor: "NFE", label: "NF-e" },
              { valor: "NFCE", label: "NFC-e" },
            ].map((m) => (
              <S.BotaoModelo
                key={m.valor}
                $ativo={modeloFiscal === m.valor}
                onClick={() => handleModeloChange(m.valor)}
              >
                {m.label}
              </S.BotaoModelo>
            ))}
          </S.LinhaModelos>
        )}

        <S.LinhaRadios>
          {operacoes.map((op) => (
            <S.ItemRadio
              key={op.valor}
              tabIndex={operacaoDesabilitada(op.valor) ? -1 : 0}
              role="radio"
              aria-checked={operacao === op.valor}
              aria-disabled={operacaoDesabilitada(op.valor)}
              $desabilitado={operacaoDesabilitada(op.valor)}
              onClick={() => !operacaoDesabilitada(op.valor) && setOperacao(op.valor)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  if (!operacaoDesabilitada(op.valor)) setOperacao(op.valor);
                }
              }}
            >
              <S.RadioCircle>
                <S.RadioDot $visivel={operacao === op.valor} />
              </S.RadioCircle>

              <S.TextoRadio>{op.label}</S.TextoRadio>
            </S.ItemRadio>
          ))}
        </S.LinhaRadios>

        <S.SecaoFormulario>{renderFormulario()}</S.SecaoFormulario>

        <div>
          <S.LabelSecao>Retorno da SEFAZ</S.LabelSecao>
          <S.AreaRetorno>
            {retornoSefaz ? (
              retornoSefaz
            ) : (
              <S.PlaceholderRetorno>
                O retorno da SEFAZ será exibido aqui após a execução.
              </S.PlaceholderRetorno>
            )}
          </S.AreaRetorno>
        </div>
      </S.Corpo>

      <S.Rodape>
        <S.BotaoCancelar onClick={cancelar}>Cancelar</S.BotaoCancelar>

        {mostrarImprimir ? (
          <S.BotaoExecutar onClick={handleImprimir} disabled={imprimindo}>
            {imprimindo ? (
              <CircularProgress size={16} style={{ color: theme.cores.textActionPrimary }} />
            ) : (
              <Print style={{ fontSize: 18 }} />
            )}
            Imprimir
          </S.BotaoExecutar>
        ) : (
          <S.BotaoExecutar onClick={executar} disabled={executando || cancelamentoConcluido}>
            {executando && (
              <CircularProgress size={16} style={{ color: theme.cores.textActionPrimary }} />
            )}

            {cancelamentoConcluido ? "NF-e já cancelada" : "Executar"}
          </S.BotaoExecutar>
        )}
      </S.Rodape>
    </S.Painel>
  );

  const confirmacao = confirmacaoAberta && (
    <S.OverlayConfirmacao>
      <S.PainelConfirmacao>
        <S.MensagemConfirmacao>
          {mensagemConfirmacaoPrincipal}
          <br />
          Esta ação é irreversível na SEFAZ.
        </S.MensagemConfirmacao>

        <S.Rodape>
          <S.BotaoCancelar autoFocus onClick={() => setConfirmacaoAberta(false)}>
            Voltar
          </S.BotaoCancelar>

          <S.BotaoExecutar
            $perigo
            onClick={() => {
              setConfirmacaoAberta(false);
              executarOperacao();
            }}
            disabled={executando}
          >
            {executando && (
              <CircularProgress size={16} style={{ color: theme.cores.textActionPrimary }} />
            )}

            {operacao === "CANCELAMENTO"
              ? "Cancelar NF-e"
              : operacao === "CCE"
                ? "Registrar carta de correção"
                : "Inutilizar"}
          </S.BotaoExecutar>
        </S.Rodape>
      </S.PainelConfirmacao>
    </S.OverlayConfirmacao>
  );

  if (isMobile) {
    return (
      <>
        <S.BottomSheetCustomizada
          open={true}
          onDismiss={() => {
            if (podeFecharAoClicarFora()) onFechar();
          }}
        >
          {conteudo}
        </S.BottomSheetCustomizada>
        {confirmacao}
      </>
    );
  }

  return (
    <S.Overlay
      onClick={() => {
        if (podeFecharAoClicarFora()) onFechar();
      }}
    >
      {conteudo}
      {confirmacao}
    </S.Overlay>
  );
}
